import { useState, useRef, useEffect } from "react";
import { supabase } from "@/lib/supabase";

export interface Recipient {
  id: string;
  phone: string;
  name?: string;
}

interface Props {
  recipients: Recipient[];
  onChange: (recipients: Recipient[]) => void;
  userId?: string;
}

const TAG_LIMIT = 10;

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
}

export default function RecipientInput({ recipients, onChange, userId }: Props) {
  const [inputValue, setInputValue] = useState("");
  const [showContacts, setShowContacts] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [contacts, setContacts] = useState<Array<{ id: string; name: string; phone: string }>>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const isBulk = recipients.length > TAG_LIMIT;

  useEffect(() => {
    if (!userId || !showContacts) return;
    let cancelled = false;
    setContactsLoading(true);
    supabase
      .from("contacts")
      .select("id, name, phone")
      .eq("user_id", userId)
      .order("name", { ascending: true })
      .then(({ data, error }) => {
        if (!cancelled) {
          setContacts(
            (data || []).map((c) => ({
              id: String(c.id),
              name: c.name || "",
              phone: c.phone || "",
            }))
          );
          if (error) console.error(error);
          setContactsLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [userId, showContacts]);

  const addRecipient = (phone: string, name?: string) => {
    const formatted = formatPhone(phone);
    if (!formatted || recipients.some((r) => r.phone === formatted)) return;
    const newR: Recipient = { id: Date.now().toString() + Math.random().toString(36).slice(2, 8), phone: formatted, name };
    onChange([...recipients, newR]);
  };

  const removeRecipient = (id: string) => {
    onChange(recipients.filter((r) => r.id !== id));
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (inputValue.trim()) {
        addRecipient(inputValue.trim());
        setInputValue("");
      }
    }
    if (e.key === "Backspace" && !inputValue && recipients.length > 0) {
      removeRecipient(recipients[recipients.length - 1].id);
    }
  };

  const handleBulkAdd = () => {
    const lines = bulkText.split(/[\n,]+/).map((l) => l.trim()).filter(Boolean);
    const newRecipients: Recipient[] = [];
    const existingPhones = new Set(recipients.map((r) => r.phone));
    lines.forEach((line) => {
      const parts = line.split(/\s+/);
      const phone = parts[0];
      const name = parts[1];
      const formatted = formatPhone(phone);
      if (formatted && !existingPhones.has(formatted) && !newRecipients.some((r) => r.phone === formatted)) {
        existingPhones.add(formatted);
        newRecipients.push({ id: Date.now().toString() + Math.random().toString(36).slice(2, 8), phone: formatted, name });
      }
    });
    onChange([...recipients, ...newRecipients]);
    setBulkText("");
    setBulkMode(false);
  };

  const removeDuplicates = () => {
    const seen = new Set<string>();
    const unique = recipients.filter((r) => {
      const key = r.phone.replace(/\D/g, "");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    onChange(unique);
  };

  const bulkLineCount = bulkText.split(/[\n,]+/).map((l) => l.trim()).filter(Boolean).length;

  const filteredContacts = contacts.filter(
    (c) =>
      !recipients.some((r) => r.phone === c.phone) &&
      (c.name?.includes(contactSearch) || c.phone.includes(contactSearch))
  );

  const filteredRecipients = listSearch
    ? recipients.filter(
        (r) =>
          (r.name && r.name.includes(listSearch)) ||
          r.phone.includes(listSearch)
      )
    : recipients;

  const hasDuplicates = (() => {
    const phones = recipients.map((r) => r.phone.replace(/\D/g, ""));
    return new Set(phones).size !== phones.length;
  })();

  return (
    <>
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-5">
          <div>
            <h3 className="text-white font-semibold text-sm md:text-base">수신자</h3>
            <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">
              {recipients.length > 0 ? (
                <span className="text-violet-400 font-medium">{recipients.length.toLocaleString()}명</span>
              ) : (
                "수신자를 추가하세요"
              )}
            </p>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <button
              onClick={() => { setBulkMode(!bulkMode); setShowContacts(false); }}
              className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1.5 rounded-lg text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                bulkMode ? "bg-violet-500/20 text-violet-400 border border-violet-500/30" : "bg-white/[0.05] text-gray-400 hover:text-white border border-white/[0.08]"
              }`}
            >
              <i className="ri-file-list-3-line text-xs md:text-sm"></i>
              대량 입력
            </button>
            <button
              onClick={() => { setShowContacts(!showContacts); setBulkMode(false); }}
              className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1.5 rounded-lg text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                showContacts ? "bg-violet-500/20 text-violet-400 border border-violet-500/30" : "bg-white/[0.05] text-gray-400 hover:text-white border border-white/[0.08]"
              }`}
            >
              <i className="ri-contacts-book-2-line text-xs md:text-sm"></i>
              주소록
            </button>
          </div>
        </div>

        {/* Compact summary for bulk recipients */}
        {isBulk && !bulkMode && (
          <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-3 md:p-4 mb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500/30 to-pink-500/30">
                  <i className="ri-group-fill text-violet-400 text-base md:text-lg"></i>
                </div>
                <div>
                  <p className="text-white text-xs md:text-sm font-semibold">{recipients.length.toLocaleString()}명 등록됨</p>
                  <p className="text-gray-500 text-[10px] md:text-xs">
                    {recipients.filter((r) => r.name).length}명 이름 있음
                    {hasDuplicates && (
                      <span className="text-amber-400 ml-2">⚠ 중복 있음</span>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowListModal(true)}
                className="flex items-center gap-1 md:gap-1.5 bg-white/[0.06] border border-white/[0.1] text-gray-300 hover:text-white px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
              >
                <i className="ri-list-check-2 text-xs md:text-sm"></i>
                목록 보기
              </button>
            </div>
            {/* Preview first 3 */}
            <div className="flex flex-wrap gap-1 md:gap-1.5 mt-2 md:mt-3">
              {recipients.slice(0, 3).map((r) => (
                <span
                  key={r.id}
                  className="inline-flex items-center gap-1 bg-white/[0.04] border border-white/[0.06] text-gray-400 text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 rounded-md"
                >
                  {r.name && <span className="text-gray-300">{r.name}</span>}
                  {r.name && <span className="text-gray-600">·</span>}
                  {r.phone}
                </span>
              ))}
              {recipients.length > 3 && (
                <span className="text-gray-600 text-[10px] md:text-xs self-center ml-1">
                  외 {recipients.length - 3}명...
                </span>
              )}
            </div>
          </div>
        )}

        {/* Tag input (for small lists) */}
        {!bulkMode && !isBulk && (
          <div
            className="min-h-[72px] md:min-h-[80px] bg-white/[0.04] border border-white/[0.1] rounded-xl p-2.5 md:p-3 flex flex-wrap gap-1.5 md:gap-2 cursor-text focus-within:border-violet-500/50 transition-all"
            onClick={() => inputRef.current?.focus()}
          >
            {recipients.map((r) => (
              <span
                key={r.id}
                className="inline-flex items-center gap-1 md:gap-1.5 bg-violet-500/20 border border-violet-500/30 text-violet-300 text-[10px] md:text-xs px-2 md:px-2.5 py-0.5 md:py-1 rounded-full whitespace-nowrap"
              >
                {r.name && <span className="text-violet-200 font-medium">{r.name}</span>}
                {r.name && <span className="text-violet-500">·</span>}
                {r.phone}
                <button
                  onClick={(e) => { e.stopPropagation(); removeRecipient(r.id); }}
                  className="w-3 h-3 md:w-3.5 md:h-3.5 flex items-center justify-center text-violet-400 hover:text-white cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-[10px] md:text-xs"></i>
                </button>
              </span>
            ))}
            <input
              ref={inputRef}
              type="tel"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onBlur={() => {
                if (inputValue.trim()) {
                  addRecipient(inputValue.trim());
                  setInputValue("");
                }
              }}
              placeholder={recipients.length === 0 ? "번호 입력 후 Enter" : "번호 추가..."}
              className="flex-1 min-w-[140px] md:min-w-[180px] bg-transparent text-white text-xs md:text-sm placeholder-gray-600 focus:outline-none"
            />
          </div>
        )}

        {/* Bulk input */}
        {bulkMode && (
          <div className="space-y-2.5 md:space-y-3">
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={"번호를 줄바꿈 또는 쉼표로 구분하여 입력하세요\n중복 번호는 자동으로 제외됩니다\n\n예:\n010-1234-5678 홍길동\n010-2345-6789 김철수\n010-3456-7890"}
              rows={6}
              className="w-full bg-white/[0.04] border border-white/[0.1] rounded-xl px-3 md:px-4 py-2.5 md:py-3 text-white text-xs md:text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all resize-none"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 md:gap-3">
                {bulkText.trim() && (
                  <span className="text-gray-400 text-[10px] md:text-xs">
                    <span className="text-violet-400 font-semibold">{bulkLineCount.toLocaleString()}</span>개 번호 감지됨
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 md:gap-2">
                <button
                  onClick={handleBulkAdd}
                  disabled={!bulkText.trim()}
                  className="flex items-center gap-1 md:gap-1.5 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 disabled:opacity-40 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-[10px] md:text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
                >
                  <i className="ri-add-circle-line text-xs md:text-sm"></i>
                  추가하기
                </button>
                <button
                  onClick={() => { setBulkMode(false); setBulkText(""); }}
                  className="px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-[10px] md:text-xs font-medium text-gray-400 hover:text-white bg-white/[0.05] cursor-pointer whitespace-nowrap transition-all"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Contact picker */}
        {showContacts && (
          <div className="mt-3 bg-[#0d0d1a] border border-white/[0.1] rounded-xl overflow-hidden">
            <div className="p-2.5 md:p-3 border-b border-white/[0.07]">
              <div className="relative">
                <div className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 w-3 h-3 md:w-4 md:h-4 flex items-center justify-center">
                  <i className="ri-search-line text-gray-500 text-xs md:text-sm"></i>
                </div>
                <input
                  type="text"
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  placeholder="이름 또는 번호 검색"
                  className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg pl-8 md:pl-9 pr-3 md:pr-4 py-1.5 md:py-2 text-white text-xs md:text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all"
                />
              </div>
            </div>
            <div className="max-h-40 md:max-h-48 overflow-y-auto">
              {contactsLoading ? (
                <div className="flex items-center justify-center py-8 md:py-10">
                  <i className="ri-loader-4-line animate-spin text-violet-400 text-lg md:text-xl"></i>
                </div>
              ) : filteredContacts.length === 0 ? (
                <p className="text-gray-500 text-xs md:text-sm text-center py-6 md:py-8">검색 결과가 없습니다</p>
              ) : (
                filteredContacts.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => { addRecipient(c.phone, c.name); }}
                    className="w-full flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-2.5 hover:bg-white/[0.05] cursor-pointer transition-colors text-left"
                  >
                    <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500/40 to-pink-500/40 text-white text-[10px] md:text-xs font-semibold shrink-0">
                      {c.name?.charAt(0) ?? "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs md:text-sm font-medium truncate">{c.name}</p>
                      <p className="text-gray-500 text-[10px] md:text-xs">{c.phone}</p>
                    </div>
                    <i className="ri-add-circle-line text-violet-400 text-sm md:text-base shrink-0"></i>
                  </button>
                ))
              )}
            </div>
            <div className="p-2.5 md:p-3 border-t border-white/[0.07] flex items-center justify-between">
              <span className="text-gray-500 text-[10px] md:text-xs">{filteredContacts.length}명의 연락처</span>
              <button
                onClick={() => {
                  filteredContacts.forEach((c) => addRecipient(c.phone, c.name));
                  setShowContacts(false);
                }}
                className="text-[10px] md:text-xs text-violet-400 hover:text-violet-300 cursor-pointer whitespace-nowrap transition-colors"
              >
                전체 추가
              </button>
            </div>
          </div>
        )}

        {/* Bottom actions */}
        {recipients.length > 0 && (
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3">
              <span className="text-gray-500 text-[10px] md:text-xs">총 {recipients.length.toLocaleString()}명 선택됨</span>
              {hasDuplicates && (
                <button
                  onClick={removeDuplicates}
                  className="flex items-center gap-1 text-[10px] md:text-xs text-amber-400 hover:text-amber-300 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-filter-off-line text-[10px] md:text-xs"></i>
                  중복 제거
                </button>
              )}
            </div>
            <button
              onClick={() => onChange([])}
              className="text-[10px] md:text-xs text-red-400/70 hover:text-red-400 cursor-pointer whitespace-nowrap transition-colors"
            >
              전체 삭제
            </button>
          </div>
        )}
      </div>

      {/* Recipient List Modal */}
      {showListModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setShowListModal(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative bg-[#0d0d1a] border border-white/[0.12] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl h-[85vh] sm:max-h-[80vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-white/[0.08] shrink-0">
              <div>
                <h3 className="text-white font-semibold text-sm md:text-base">수신자 목록</h3>
                <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">
                  총 <span className="text-violet-400 font-medium">{recipients.length.toLocaleString()}</span>명
                </p>
              </div>
              <button
                onClick={() => setShowListModal(false)}
                className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors rounded-lg hover:bg-white/[0.05]"
              >
                <i className="ri-close-line text-base md:text-lg"></i>
              </button>
            </div>

            {/* Search + actions */}
            <div className="px-4 md:px-6 py-2.5 md:py-3 border-b border-white/[0.06] flex items-center gap-2 md:gap-3 shrink-0">
              <div className="relative flex-1">
                <div className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 w-3 h-3 md:w-4 md:h-4 flex items-center justify-center">
                  <i className="ri-search-line text-gray-500 text-xs md:text-sm"></i>
                </div>
                <input
                  type="text"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                  placeholder="이름 또는 번호로 필터링"
                  className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg pl-8 md:pl-9 pr-3 md:pr-4 py-1.5 md:py-2 text-white text-xs md:text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all"
                />
              </div>
              {listSearch && (
                <button
                  onClick={() => setListSearch("")}
                  className="text-[10px] md:text-xs text-gray-400 hover:text-white cursor-pointer whitespace-nowrap transition-colors"
                >
                  초기화
                </button>
              )}
            </div>

            {/* Recipient table */}
            <div className="flex-1 overflow-y-auto">
              {filteredRecipients.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 md:py-16 gap-2 md:gap-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/[0.03]">
                    <i className="ri-user-search-line text-gray-600 text-lg md:text-xl"></i>
                  </div>
                  <p className="text-gray-500 text-xs md:text-sm">검색 결과가 없습니다</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="sticky top-0 bg-[#0d0d1a] z-10">
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left text-gray-500 text-[10px] md:text-xs font-medium px-4 md:px-6 py-2 md:py-2.5 w-10 md:w-12">#</th>
                      <th className="text-left text-gray-500 text-[10px] md:text-xs font-medium px-0 py-2 md:py-2.5">이름</th>
                      <th className="text-left text-gray-500 text-[10px] md:text-xs font-medium px-0 py-2 md:py-2.5">전화번호</th>
                      <th className="text-right text-gray-500 text-[10px] md:text-xs font-medium px-4 md:px-6 py-2 md:py-2.5 w-12 md:w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecipients.map((r, idx) => (
                      <tr key={r.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors group">
                        <td className="px-4 md:px-6 py-2 md:py-2.5 text-gray-600 text-[10px] md:text-xs">{idx + 1}</td>
                        <td className="px-0 py-2 md:py-2.5">
                          <span className="text-white text-xs md:text-sm">{r.name || "-"}</span>
                        </td>
                        <td className="px-0 py-2 md:py-2.5">
                          <span className="text-gray-300 text-xs md:text-sm font-mono">{r.phone}</span>
                        </td>
                        <td className="px-4 md:px-6 py-2 md:py-2.5 text-right">
                          <button
                            onClick={() => removeRecipient(r.id)}
                            className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center text-gray-600 hover:text-red-400 cursor-pointer transition-colors"
                          >
                            <i className="ri-close-line text-xs md:text-sm"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-between px-4 md:px-6 py-2.5 md:py-3 border-t border-white/[0.08] shrink-0 bg-[#0d0d1a]">
              <span className="text-gray-500 text-[10px] md:text-xs">
                {listSearch
                  ? `${filteredRecipients.length.toLocaleString()}명 표시 중 (전체 ${recipients.length.toLocaleString()}명)`
                  : `전체 ${recipients.length.toLocaleString()}명`}
              </span>
              <button
                onClick={() => setShowListModal(false)}
                className="px-3 md:px-4 py-1.5 md:py-2 rounded-lg bg-white/[0.06] border border-white/[0.08] text-gray-300 hover:text-white text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}